import { ColumnDef } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { TvController } from '@/lib/schema/tvController';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { deleteTvController } from '@/lib/api/tvController';
import { GUIDE_STATE_LABEL, GuideState } from '@/lib/schema/guideSync';

/** A TV row with its guide state against its group's guide (see guideSync). */
export type TvControllerRow = TvController & { guideState?: GuideState };

const GUIDE_STATE_CLASS: Record<GuideState, string> = {
  ok: 'text-muted-foreground',
  outdated: 'text-amber-600 font-medium',
  unknown: 'text-muted-foreground',
  pending: 'text-muted-foreground',
  none: 'text-muted-foreground',
  conflict: 'text-destructive font-medium',
};

export const columns: ColumnDef<TvControllerRow>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomeRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <div
        onClick={() =>
          column.toggleSorting(column.getIsSorted() == 'asc', false)
        }
      >
        Name
      </div>
    ),
    cell: ({ row }) => <div className="capitalize">{row.getValue('name')}</div>,
  },
  {
    accessorKey: 'ip',
    header: 'IP',
    cell: ({ row }) => <div className="capitalize">{row.getValue('ip')}</div>,
  },
  {
    accessorKey: 'macAddress',
    header: 'MAC Address',
    cell: ({ row }) => (
      <div className="capitalize">{row.getValue('macAddress') || '--'}</div>
    ),
  },
  {
    accessorKey: 'firmwareVersion',
    header: 'Firmware',
    cell: ({ row }) => (
      <div className="capitalize">
        {row.getValue('firmwareVersion') || '--'}
      </div>
    ),
  },
  {
    accessorKey: 'volume',
    header: 'Volume',
    cell: ({ row }) => (
      <div className="capitalize">{row.getValue('volume') || '--'}</div>
    ),
  },
  {
    accessorKey: 'channel',
    header: 'Channel',
    cell: ({ row }) => (
      <div className="capitalize">
        {row.original.staticImageOn ? 'IMG' : row.getValue('channel') || '--'}
      </div>
    ),
  },
  {
    accessorKey: 'guideState',
    header: 'Guide',
    cell: ({ row }) => {
      const state = row.getValue('guideState') as GuideState | undefined;
      if (!state) return <div className="text-xs">--</div>;
      return (
        <div
          className={`whitespace-nowrap text-xs ${GUIDE_STATE_CLASS[state]}`}
          title={
            state === 'conflict'
              ? 'This TV is in two groups with different channel guides'
              : state === 'unknown'
                ? 'No guide loaded through the dashboard yet; use Reload guide on its group'
                : undefined
          }
        >
          {GUIDE_STATE_LABEL[state]}
        </div>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge className="capitalize">{row.getValue('status')}</Badge>
    ),
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'tags',
    header: 'Tags',
    filterFn: (row, id, value) => {
      return value.some((v: string) =>
        (row.getValue(id) as string[]).includes(v)
      );
    },
  },
  {
    accessorKey: 'id',
    header: '',
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link
                className="cursor-pointer"
                to={`/tv-controllers/${row.getValue('id')}`}
              >
                Edit
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => deleteTvController(row.getValue('id'))}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
